import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APP_VERSION, config } from '@/environment';
import { PRIVACY_CONTACT_EMAIL } from '@library/constants/legal';

// One source for the address, shared with the app and the legal documents. It
// was hardcoded here as info@cwebspace.com while the documents said something
// else — see the note on PRIVACY_CONTACT_EMAIL for why that matters.
const CONTACT_EMAIL = PRIVACY_CONTACT_EMAIL;

export const SiteFooter: React.FC = () => {
    const { t } = useTranslation();
    const year = new Date().getFullYear();

    return (
        <footer className="sitefoot">
            <div className="wrap">
                <div className="sitefoot__cols">
                    <div>
                        <Link className="brand" to="/">
                            <span className="brand__mark" aria-hidden="true">🚌</span>
                            Bus Line Recorder
                        </Link>
                        <p className="sitefoot__blurb" style={{ marginTop: 10 }}>
                            {t('web.footer.blurb')}
                        </p>
                    </div>

                    <div>
                        <div className="sitefoot__title">{t('web.footer.product')}</div>
                        <div className="sitefoot__links">
                            <Link to="/#features">{t('web.nav.features')}</Link>
                            <Link to="/#apps">{t('web.nav.apps')}</Link>
                            <Link to="/#plans">{t('web.nav.plans')}</Link>
                            <Link to="/#whatsnew">{t('web.nav.whatsnew')}</Link>
                        </div>
                    </div>

                    <div>
                        <div className="sitefoot__title">{t('web.footer.company')}</div>
                        <div className="sitefoot__links">
                            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                            <Link to="/privacy">{t('web.footer.privacy')}</Link>
                            <Link to="/terms">{t('web.footer.terms')}</Link>
                            {/* Every dunning email links straight here, but the page
                                also has to be findable by somebody who did not get
                                one -- a help page you can only reach from a payment
                                demand reads as part of the demand. */}
                            <Link to="/billing-help">{t('web.footer.billing')}</Link>
                            {/* Findable without an order confirmation in hand, for
                                the same reason: a right whose only route is a link
                                inside a receipt is a right you have to have kept
                                the receipt to use. */}
                            <Link to="/withdrawal">{t('web.footer.withdrawal')}</Link>
                            {/* Both are aimed at company customers rather than at
                                drivers, and both are in the footer for the same
                                reason as the two above: the agreement promises 30
                                days' notice before a sub-processor changes, and a
                                notice is only checkable if the list has a route
                                that does not run through a contract or an email
                                somebody still has. */}
                            <Link to="/dpa">{t('web.footer.dpa')}</Link>
                            <Link to="/subprocessors">{t('web.footer.subprocessors')}</Link>
                            <Link to="/help">{t('web.footer.help')}</Link>
                            <Link to="/credits">{t('web.footer.credits')}</Link>
                        </div>
                    </div>
                </div>

                <div className="sitefoot__bottom">
                    <span>© {year} CWebSpace. {t('web.footer.rights')}</span>
                    {/* Build identity, so a support reply can ask "what does the
                        footer say" instead of guessing which bundle is live. */}
                    <span>v{APP_VERSION}{config.name === 'production' ? '' : ` · ${config.name}`}</span>
                </div>
            </div>
        </footer>
    );
};
